import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, AlertTriangle, Activity, LucideIcon, ChevronLeft, ChevronRight, Wallet } from 'lucide-react';

interface ExecutionStatus {
  status: 'idle' | 'running' | 'completed' | 'failed';
  started_at?: string;
  completed_at?: string;
  failed_at?: string;
  message?: string;
}

export interface SidebarMenuItem {
  id: string;
  icon?: LucideIcon;
  imageUrl?: string;
  label: string;
  description: string;
  color: string;
  status?: ExecutionStatus;
  disabled?: boolean;
}

export interface SidebarHeaderConfig {
  title: string;
  subtitle: string;
  icon?: LucideIcon;
  imageUrl?: string;
  iconColor?: string;
}

export interface SidebarConfig {
  header?: SidebarHeaderConfig;
  menuItems: SidebarMenuItem[];
  showStatusFooter?: boolean;
  statusFooterText?: string;
  width?: number; // Width in pixels (for dynamic resizing)
}

// Helper function to convert pixel width to margin
export const getMarginFromWidth = (width: number): number => {
  return width;
};

interface SimulationSidebarProps {
  activeSection: 'wallet' | 'download' | 'alerts' | 'rtc';
  onSectionChange: (section: 'wallet' | 'download' | 'alerts' | 'rtc') => void;
  downloadStatus: ExecutionStatus;
  alertStatus: ExecutionStatus;
  rtcStatus?: ExecutionStatus;
  config?: Partial<SidebarConfig>;
  width?: number;
  onWidthChange?: (width: number) => void;
}

const SimulationSidebar: React.FC<SimulationSidebarProps> = ({
  activeSection,
  onSectionChange,
  downloadStatus,
  alertStatus,
  rtcStatus,
  config
}) => {
  // Collapsed state
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('simulationSidebarCollapsed');
      return stored === 'true';
    } catch {
      return false;
    }
  });

  // Fixed widths
  const expandedWidth = 280; // Fixed width when expanded
  const collapsedWidth = 64; // Width when collapsed (icons only)
  const sidebarWidth = isCollapsed ? collapsedWidth : expandedWidth;

  // Toggle collapsed state
  const toggleCollapsed = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    try {
      localStorage.setItem('simulationSidebarCollapsed', newCollapsed.toString());
    } catch (e) {
}
  };

  // Default configuration
  const defaultConfig: SidebarConfig = {
    header: {
      title: 'Simulation-On-Demand',
      subtitle: 'Data Operations',
      icon: Activity,
      iconColor: '#0084CF'
    },
    menuItems: [
      {
        id: 'wallet',
        icon: Wallet,
        label: 'Simulation-On-Demand',
        description: 'Data Operations',
        color: '#0084CF',
        status: { status: 'idle' }
      },
      {
        id: 'download',
        icon: Download,
        label: 'Download Raw Data',
        description: 'To local',
        color: '#0084CF',
        status: downloadStatus
      },
      {
        id: 'alerts',
        icon: AlertTriangle,
        label: 'Generate Alerts',
        description: 'SCB, Inverters, etc',
        color: '#0084CF',
        status: alertStatus
      },
      {
        id: 'rtc',
        imageUrl: '/rtc-optimization-logo.png',
        label: 'Run RTC Optimization',
        description: 'Automatic and Manual',
        color: '#0084CF',
        status: rtcStatus
      }
    ],
    showStatusFooter: true,
    statusFooterText: 'System Status',
    width: sidebarWidth
  };

  // Merge user config with defaults
  const sidebarConfig: SidebarConfig = {
    header: {
      ...defaultConfig.header!,
      ...(config?.header || {})
    },
    menuItems: config?.menuItems || defaultConfig.menuItems.map((item) => {
      // Map status to menu items if not provided
      if (item.id === 'download' && !item.status) {
        return { ...item, status: downloadStatus };
      }
      if (item.id === 'alerts' && !item.status) {
        return { ...item, status: alertStatus };
      }
      if (item.id === 'rtc' && !item.status) {
        return { ...item, status: rtcStatus };
      }
      return item;
    }),
    showStatusFooter: config?.showStatusFooter !== undefined ? config.showStatusFooter : defaultConfig.showStatusFooter,
    statusFooterText: config?.statusFooterText || defaultConfig.statusFooterText,
    width: sidebarWidth
  };

  const menuItems = sidebarConfig.menuItems;

  const getStatusBadge = (status: ExecutionStatus) => {
    switch (status.status) {
      case 'running':
        return (
          <motion.div
            className="w-1.5 h-1.5 rounded-full bg-[#0084CF]"
            animate={{ scale: [1, 1.3, 1], opacity: [1, 0.6, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        );
      case 'completed':
        return <div className="w-1.5 h-1.5 rounded-full bg-[#1E7F4F]" />;
      case 'failed':
        return <div className="w-1.5 h-1.5 rounded-full bg-[#B42318]" />;
      default:
        return null;
    }
  };

  return (
    <>
      <div
        data-tour="sim-nav"
        className="fixed left-[3.5rem] top-16 bottom-0 bg-white border-r border-gray-100 transition-all duration-200"
        style={{ width: `${sidebarWidth}px` }}
      >
        {/* Toggle Button - Absolute positioned to save space */}
        <button
          onClick={toggleCollapsed}
          className={`absolute top-4 z-30 w-6 h-6 rounded-md hover:bg-gray-100 flex items-center justify-center transition-all duration-200 ${
            isCollapsed 
              ? '-right-3 bg-white shadow-sm border border-gray-100' 
              : 'right-2'
          }`}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight size={16} className="text-gray-600" />
          ) : (
            <ChevronLeft size={16} className="text-gray-600" />
          )}
        </button>

        {/* Clean Navigation Menu */}
        <nav className={`h-full overflow-y-auto scrollable pb-20 py-2 ${isCollapsed ? 'px-1 pt-12' : 'px-2 pt-12'}`}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;

            return (
              <motion.button
                key={item.id}
                onClick={() => !item.disabled && onSectionChange(item.id as 'wallet' | 'download' | 'alerts' | 'rtc')}
                disabled={item.disabled}
                className={`w-full flex items-center gap-2 transition-all duration-200 relative group rounded-lg mb-1 ${item.disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : isActive
                    ? ''
                    : 'hover:bg-gray-50'
                  } ${isCollapsed ? 'px-2 py-3 justify-center' : 'px-3 py-3'}`}
                style={{
                  backgroundColor: isActive ? `${item.color}26` : 'transparent',
                }}
                whileHover={item.disabled ? {} : { x: isActive || isCollapsed ? 0 : 3 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
              >
                {/* Active Indicator Dot */}
                {isActive && (
                  <div 
                    className={`absolute rounded-full bg-[#1E7F4F] shadow-[0_0_8px_rgba(26,170,7,0.4)] ${
                      isCollapsed ? 'right-1.5 bottom-1.5 w-2 h-2' : 'right-3 w-2 h-2'
                    }`}
                    style={{ zIndex: 10 }}
                  />
                )}

                {/* Icon */}
                <div
                  className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 ${isActive
                    ? 'shadow-sm'
                    : 'bg-gray-100'
                    }`}
                  style={{
                    backgroundColor: isActive ? item.color : undefined
                  }}
                >
                  {item.imageUrl ? (
                    <img 
                      src={item.imageUrl} 
                      alt={item.label} 
                      className="w-[30px] h-auto object-contain mix-blend-multiply"
                    />
                  ) : Icon ? (
                    <Icon
                      size={18}
                      className={isActive ? 'text-white' : 'text-gray-600'}
                      strokeWidth={2.5}
                    />
                  ) : null}
                </div>

                {/* Content - Hidden when collapsed */}
                {!isCollapsed && (
                  <div className="flex-1 text-left min-w-0 pr-4">
                    <h3
                      className={`font-black text-[12.5px] uppercase tracking-tight ${isActive ? 'text-[#000000]' : 'text-gray-950'}`}
                      style={{ color: '#16202B', fontWeight: 1000, lineHeight: '1.2' }}
                    >
                      {item.label}
                    </h3>
                    <p className="text-[10px] text-gray-950 mt-0.5 font-black uppercase tracking-tight" style={{ color: '#16202B', fontWeight: 900, lineHeight: '1.2' }}>
                      {item.description}
                    </p>
                  </div>
                )}

                {/* Status badge when collapsed - show on icon (only for non-active items) */}
                {isCollapsed && !isActive && item.status && (
                  <div className="absolute top-1 right-1">
                    {getStatusBadge(item.status)}
                  </div>
                )}
              </motion.button>
            );
          })}
        </nav>

        {/* Compact Status Footer */}
        {sidebarConfig.showStatusFooter && (
          <div className={`absolute bottom-0 left-0 right-0 border-t border-gray-100 bg-gray-50/50 py-3 ${isCollapsed ? 'px-2' : 'px-3'}`}>
            {isCollapsed ? (
              <div className="flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-[#1E7F4F] animate-pulse" />
              </div>
            ) : (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-950 font-black uppercase tracking-tight" style={{ color: '#16202B', fontWeight: 1000 }}>{sidebarConfig.statusFooterText}</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#1E7F4F] animate-pulse" />
                  <span className="text-gray-950 font-black uppercase tracking-tight" style={{ color: '#16202B', fontWeight: 1000 }}>Online</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SimulationSidebar;

